/**
 * Vanilla Cart State Manager
 * Replaces Rivets.js with a lightweight reactive state management system
 * Handles cart data, DOM bindings, and automatic UI updates
 */

class CartState {
    constructor() {
        this.cart = {
            item_count: 0,
            items: [],
            total_price: 0,
            total_discount: 0,
            total_compare_at_savings: 0,
            currency: 'USD',
            cart_level_discount_applications: []
        };

        this.subscribers = new Map();
        // Use store's money format (set globally in theme.liquid)
        this.moneyFormat = window.money_format || window.theme?.settings?.moneyFormat || '{{amount}}';
    }

    /**
     * Fetch cart data from Shopify
     * @returns {Promise<Object>} Cart data
     */
    async fetchCart() {
        try {
            const response = await fetch('/cart.js', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const cart = await response.json();
            this.updateCart(cart);
            return cart;
        } catch (error) {
            console.error('Error fetching cart:', error);
            throw error;
        }
    }

    /**
     * Update cart state and notify subscribers
     * @param {Object} cartData - New cart data
     */
    updateCart(cartData) {
        // Enrich cart data with calculated fields
        this.cart = this.enrichCartData(cartData);

        // Notify all subscribers
        this.notifySubscribers();

        // Dispatch custom event for other components
        this.dispatchCartEvent('cart:updated', this.cart);
    }

    /**
     * Enrich cart data with calculated fields
     * @param {Object} cart - Raw cart data
     * @returns {Object} Enriched cart data
     */
    enrichCartData(cart) {
        // Calculate total compare-at savings
        let totalCompareAtSavings = 0;

        if (cart.items) {
            cart.items = cart.items.map(item => {
                // compare_at_price may be on item or item.variant (cart.js varies)
                const compareAtPrice = item.compare_at_price ?? item.variant?.compare_at_price ?? 0;

                // Calculate compare-at savings per item
                let itemCompareAtSavings = 0;

                if (compareAtPrice && compareAtPrice > item.price) {
                    itemCompareAtSavings = (compareAtPrice - item.price) * item.quantity;
                    totalCompareAtSavings += itemCompareAtSavings;
                }

                // Calculate line-level compare-at price
                const compare_at_line_price = compareAtPrice ? compareAtPrice * item.quantity : 0;

                return {
                    ...item,
                    compare_at_line_price,
                    item_compare_at_savings: itemCompareAtSavings
                };
            });
        }

        return {
            ...cart,
            total_compare_at_savings: totalCompareAtSavings
        };
    }

    /**
     * Subscribe an element to cart state updates
     * @param {HTMLElement} element - DOM element to bind
     * @param {Object} binding - Binding configuration
     */
    subscribe(element, binding) {
        if (!element) return;

        // Store binding config
        if (!this.subscribers.has(element)) {
            this.subscribers.set(element, []);
        }
        this.subscribers.get(element).push(binding);

        // Apply initial value
        this.updateElement(element, binding);
    }

    /**
     * Unsubscribe an element from updates
     * @param {HTMLElement} element - Element to unsubscribe
     */
    unsubscribe(element) {
        this.subscribers.delete(element);
    }

    /**
     * Update a single element based on its binding
     * @param {HTMLElement} element - Element to update
     * @param {Object} binding - Binding configuration
     */
    updateElement(element, binding) {
        const { type, path, filter, formatter } = binding;
        let value = this.getValueByPath(path);

        // Apply filter if specified
        if (filter) {
            value = this.applyFilter(value, filter);
        }

        // Apply formatter if specified
        if (formatter) {
            value = formatter(value);
        }

        // Update element based on binding type
        switch (type) {
            case 'text':
                element.textContent = value ?? '';
                break;

            case 'html':
                element.innerHTML = value ?? '';
                break;

            case 'value':
                if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.tagName === 'SELECT') {
                    element.value = value ?? '';
                }
                break;

            case 'show':
                element.style.display = value ? '' : 'none';
                break;

            case 'hide':
                element.style.display = value ? 'none' : '';
                break;

            case 'attr':
                if (binding.attr) {
                    element.setAttribute(binding.attr, value ?? '');
                }
                break;

            case 'class':
                if (binding.className) {
                    if (value) {
                        element.classList.add(binding.className);
                    } else {
                        element.classList.remove(binding.className);
                    }
                }
                break;
        }
    }

    /**
     * Get value from cart by path (e.g., "cart.item_count")
     * @param {string} path - Dot-notation path
     * @returns {*} Value at path
     */
    getValueByPath(path) {
        const parts = path.split('.');
        let value = { cart: this.cart };

        for (const part of parts) {
            if (value === null || value === undefined) {
                return undefined;
            }
            value = value[part];
        }

        return value;
    }

    /**
     * Apply filter to value
     * @param {*} value - Input value
     * @param {string|Object} filter - Filter name or config
     * @returns {*} Filtered value
     */
    applyFilter(value, filter) {
        if (typeof filter === 'string') {
            return this.filters[filter]?.(value) ?? value;
        }

        if (typeof filter === 'object') {
            const { name, args } = filter;
            return this.filters[name]?.(value, ...args) ?? value;
        }

        return value;
    }

    /**
     * Built-in filters (similar to Rivets.js formatters)
     */
    filters = {
        eq: (value, compare) => value === compare,
        neq: (value, compare) => value !== compare,
        gt: (value, compare) => value > compare,
        lt: (value, compare) => value < compare,
        gte: (value, compare) => value >= compare,
        lte: (value, compare) => value <= compare,
        not: (value) => !value,
        money: (value) => this.formatMoney(value),
        img_url: (url, size) => {
            if (!url) return '';
            if (size) {
                return url.replace(/\.(jpg|jpeg|png|gif|webp)/i, `_${size}.$1`);
            }
            return url;
        }
    };

    /**
     * Format money value
     * @param {number} cents - Amount in cents
     * @param {string} currency - Currency code (optional)
     * @returns {string} Formatted money string
     */
    formatMoney(cents) {
        if (cents == null) return '';

        // Use Shopify.formatMoney with store's money format
        if (typeof Shopify?.formatMoney === 'function') {
            const format = window.money_format || this.moneyFormat;
            return Shopify.formatMoney(cents, format);
        }

        // Fallback: manual formatting
        const amount = (cents / 100).toFixed(2);
        const format = window.money_format || this.moneyFormat || '{{amount}}';
        return format.replace(/\{\{\s*amount\s*\}\}/g, amount)
            .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100))
            .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','));
    }

    /**
     * Notify all subscribers of state change
     */
    notifySubscribers() {
        this.subscribers.forEach((bindings, element) => {
            bindings.forEach(binding => {
                this.updateElement(element, binding);
            });
        });
    }

    /**
     * Dispatch custom cart event
     * @param {string} eventName - Event name
     * @param {*} detail - Event detail data
     */
    dispatchCartEvent(eventName, detail) {
        const event = new CustomEvent(eventName, {
            detail,
            bubbles: true,
            cancelable: true
        });
        document.dispatchEvent(event);
    }

    /**
     * Add item to cart
     * @param {number|string} variantId - Variant ID
     * @param {number} quantity - Quantity to add
     * @param {Object} properties - Line item properties
     * @returns {Promise<Object>} Updated cart
     */
    async addItem(variantId, quantity = 1, properties = {}) {
        try {
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: variantId,
                    quantity,
                    properties
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.description || error.message || 'Failed to add item');
            }

            // Fetch updated cart
            return await this.fetchCart();
        } catch (error) {
            console.error('Error adding item to cart:', error);
            throw error;
        }
    }

    /**
     * Update item quantity in cart
     * @param {number|string} variantId - Variant ID
     * @param {number} quantity - New quantity
     * @returns {Promise<Object>} Updated cart
     */
    async updateItemById(variantId, quantity) {
        try {
            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: variantId,
                    quantity
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.description || error.message || 'Failed to update item');
            }

            // Fetch full cart so cart:updated gets complete data (progress bar, gift, addons)
            const cart = await this.fetchCart();
            return cart;
        } catch (error) {
            console.error('Error updating cart item:', error);
            throw error;
        }
    }

    /**
     * Update item by line number
     * @param {number} line - Line number (1-indexed)
     * @param {number} quantity - New quantity
     * @returns {Promise<Object>} Updated cart
     */
    async updateItemByLine(line, quantity) {
        try {
            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    line,
                    quantity
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.description || error.message || 'Failed to update item');
            }

            // Fetch full cart so cart:updated gets complete data (progress bar, gift, addons)
            const cart = await this.fetchCart();
            return cart;
        } catch (error) {
            console.error('Error updating cart item:', error);
            throw error;
        }
    }

    /**
     * Set or remove cart discount (empty string = remove)
     * @param {string} discount - Discount code, or '' to remove
     * @returns {Promise<Object>} Updated cart
     */
    async setDiscount(discount) {
        try {
            const response = await fetch('/cart/update.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ discount: discount || '' })
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.description || err.message || 'Failed to update discount');
            }

            const cart = await response.json();
            this.updateCart(cart);
            return cart;
        } catch (error) {
            console.error('Error updating discount:', error);
            throw error;
        }
    }

    /**
     * Set cart note
     * @param {string} note - Cart note
     * @returns {Promise<Object>} Updated cart
     */
    async setNote(note) {
        try {
            const response = await fetch('/cart/update.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ note })
            });

            if (!response.ok) {
                throw new Error('Failed to update cart note');
            }

            const cart = await response.json();
            this.updateCart(cart);
            return cart;
        } catch (error) {
            console.error('Error setting cart note:', error);
            throw error;
        }
    }

    /**
     * Clear cart
     * @returns {Promise<Object>} Empty cart
     */
    async clearCart() {
        try {
            const response = await fetch('/cart/clear.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error('Failed to clear cart');
            }

            const cart = await response.json();
            this.updateCart(cart);
            return cart;
        } catch (error) {
            console.error('Error clearing cart:', error);
            throw error;
        }
    }
}

// Create global instance
window.VanillaCartState = window.VanillaCartState || new CartState();

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
    module.exports = CartState;
}
